/**
 * What the meeting record SAYS — the pure half of `/meetings/[id]`'s record
 * (#13808, canvas C1/C4/C5/C6, Decision 02 of #13791).
 *
 * The record page is the surface the whole epic delivers, and almost every
 * decision on it is a phrasing decision rather than a rendering one: which
 * string is the title, which string is evidence beneath it, what an absent
 * due date is called, and when an item stops carrying an AI label. Those
 * decisions live here, with no React and no Prisma, so they can be asserted
 * against literal rows instead of through a tree.
 *
 * ## The reading is the title; the quote is evidence UNDER it
 *
 * #13791's rules are "label it, cite it, reverse it". A card whose title is
 * the transcript quote has cited without reading — which is the withdrawn
 * no-recommendation posture #13695 shipped and #13796 replaced. So
 * {@link buildRecordCards} puts the `Belief` statement in `reading` and the
 * transcript's own characters in `quotedText`, and a card with no
 * interpretation falls back to the row's own words WITHOUT also repeating
 * them as a quote: citing a string to itself is a citation that proves
 * nothing.
 *
 * ## Accepting removes the AI label
 *
 * That single act is both the audit trail and the conversion event. An
 * ACCEPTED extraction is a `Task` a person confirmed, so it is no longer a
 * machine's proposal and must not keep saying it is. `aiLabelled` is
 * therefore derived from `(source, state)` and never stored.
 *
 * ## Topic assignment is DERIVED, and admits when it cannot decide
 *
 * `MeetingSummarySection` carries a heading and a paragraph and no transcript
 * span (#13807), so there is no stored edge from an item to a topic. Banding
 * the timeline into equal slices would manufacture one. {@link assignCardsToTopics}
 * instead scores shared vocabulary between the card and the section, and a
 * card that matches nothing goes to an UNFILED group rather than being
 * pushed into the nearest heading — on a district record, "which topic was
 * this under" is not worth a guess, and the item still renders either way.
 */

import type { RecordItemProvenance } from '@/lib/meetings/transcript-view-model'
import type {
  MeetingRecordItem,
  MeetingRecordItemKind,
  MeetingRecordItemSource,
  MeetingRecordItemState,
} from '@/lib/meetings/record-item-model'
import type {
  MeetingSummaryCounts,
  MeetingSummarySection,
} from '@/lib/meetings/meeting-summary-model'
import {
  isTranscriptionInFlight,
  type MeetingTranscriptionStatus,
} from '@/lib/meetings/transcription-status'
import { DEFAULT_ITEM_STATE } from '@/lib/item-states'
import {
  type LinkedPriority,
  planGoalFromLinks,
} from '@/lib/notetaker/accepted-priority'
import { presentReading } from '@/lib/notetaker/present-reading'
import { commitmentLabel } from '@/lib/notetaker/commitment-label'
import type { DecisionFormalityRead } from '@/lib/decisions/formality'
import { decisionCardFormality } from '@/lib/meetings/record-card-formality'

/**
 * What a card says where the item has no due date.
 *
 * A RENDERED VALUE, not an empty state, and the issue says so explicitly.
 * Blank space reads as "the page failed to load a date"; this reads as a
 * fact about the item.
 *
 * Says "no due date" rather than the "no date said" this used to spell
 * (#13869). Both are true, but they are about different things: "no date
 * said" is a claim about the TRANSCRIPT, and the reader is looking at the
 * ITEM. Making them resolve a sentence about what was recorded in order to
 * learn that this item has no date is one indirection for no gain — and it
 * is wrong outright on a hand-added row, which no transcript ever mentioned.
 */
export const NO_DUE_DATE = 'no due date'

/** The same posture for an owner nobody named, on an EXTRACTED row. */
export const NO_OWNER_SUGGESTED = 'no owner suggested'

/**
 * The same, where nobody was ever asked to suggest one (#14044).
 *
 * "no owner suggested" is a claim about an extraction that named nobody. On
 * a hand-added row there was no extractor and no suggestion — the composer
 * offered an owner picker and the person left it empty — so the honest
 * sentence is about the item, not about a step that never ran.
 */
export const NO_OWNER = 'no owner'

/**
 * The interpretation hanging off one extracted span (#13796).
 *
 * Read from `Belief` + `BeliefLink` by `record-interpretations.ts`; shaped
 * here so this module stays free of Prisma and so a card can be built in a
 * test from four literals.
 */
export type RecordItemInterpretation = {
  /** The model's reading of the span — the card's TITLE. */
  statement: string
  /** `Belief.confidence`, inside [0,1]. Null when the row carries none. */
  confidence: number | null
  /** The suggested owner's display name. Never "owes", never "committed". */
  suggestedOwnerName: string | null
  /** `Person.id` of that suggested owner — what C6 compares the viewer to. */
  suggestedOwnerPersonId: string | null
  /** The plan goal the reading counts toward, from `BeliefLink → Priority`. */
  planGoalTitle: string | null
  /**
   * That goal's `Priority.id`.
   *
   * Carried alongside the title because the rail groups by goal, and two
   * goals in one org may legitimately share a title — grouping on the
   * display string would silently merge them and report one meeting's
   * contribution against the wrong plan.
   */
  planGoalId: string | null
  /**
   * Every Priority the reading links to, OLDEST FIRST (#14239).
   *
   * Since ADR-0112 § 5 an accepted ACTION's reading links to the Priority
   * accepting minted as well as the plan goal extraction named — and a
   * person may link it to others by hand — so `planGoalId`/`planGoalTitle`
   * above, which take the first link, cannot tell them apart.
   * {@link buildRecordCards} picks the goal out of this list by the item's
   * own `priorityId` (`planGoalFromLinks`). Absent on an interpretation
   * built before the field existed, which then reads exactly as it did.
   *
   * NOT where the minted Priority is found: that is
   * `MeetingRecordItem.acceptedPriority`, read through the
   * `PriorityExtractedItem` minted-from edge.
   */
  linkedPriorities?: readonly LinkedPriority[]
}

/** One item card, with every string it renders already decided. */
/** One further hearing of a card's item, as the card shows it. */
export type RecordCardCorroboratingQuote = {
  quotedText: string
  /** Who recorded it, or null when their name could not be read. */
  recorderName: string | null
  /** Where in THAT recording, or null when it has no position. */
  timecodeLabel: string | null
}

export type RecordCard = {
  id: string
  source: MeetingRecordItemSource
  kind: MeetingRecordItemKind | null
  state: MeetingRecordItemState
  /**
   * The reading, or the row's own words when nothing interpreted it —
   * PRESENTED (#14244): sentence case, unquoted, trimmed, no terminal period,
   * through `presentReading`. Never the raw string; see
   * {@link RecordCard.readingFromQuote} for the fallback.
   */
  reading: string
  /**
   * The heading is the transcript's own words standing in for a reading
   * that was never produced (or was refused, ADR-0104 § 6) — so the card
   * marks it as a quote rather than presenting it as an interpretation.
   * Only ever true on an `EXTRACTION` row. Optional only so card literals
   * written before it existed still type-check; the builder always sets it.
   */
  readingFromQuote?: boolean
  /**
   * The transcript's own characters, as EVIDENCE under the reading.
   *
   * Null when `reading` already IS those characters — a quote of itself is
   * not a citation, and rendering it twice implies a reading that was never
   * produced.
   */
  quotedText: string | null
  timecodeLabel: string | null
  /**
   * The same item heard in OTHER captures of a shared event note (#14765,
   * ADR-0112 § 10.4) — `ExtractedItem.corroboratingQuotes`, each naming its
   * recorder. Absent or empty on every card heard once, which is every card
   * outside a shared event note. Optional so the builder and every card
   * literal written before it stay valid; the note page attaches it.
   */
  corroboratingQuotes?: readonly RecordCardCorroboratingQuote[]
  /**
   * Always a sentence; see {@link NO_OWNER_SUGGESTED} and {@link NO_OWNER}.
   *
   * RENAMED from `suggestedOwnerLabel` (#14044). The old name asserted the
   * provenance of the value in the value's own name, and it was wrong for
   * two of the three arms: a `MANUAL` row's owner is the person the author
   * picked from the roster, and nothing suggested them. A surface reading a
   * field called `suggestedOwnerLabel` will eventually print the word
   * "suggested" beside it — one did, until #13972 removed the line.
   *
   * A companion `ownerIsSuggestion: boolean` was added here and removed again
   * in review: nothing read it, and the fallback VALUE already carries the
   * distinction a renderer needs — {@link NO_OWNER_SUGGESTED} on the arm with
   * an extractor, {@link NO_OWNER} on the arms without one. Add the flag back
   * when a surface actually frames the owner, not before.
   */
  ownerLabel: string
  /**
   * `Person.id` of the suggested owner, or null when the reading named
   * nobody the roster could resolve.
   *
   * Carried so the rail can count per attendee without re-consulting the
   * interpretation map, and so C6's marker and the rail's counts are derived
   * from ONE value. Two derivations of "whose item is this" is how a card
   * that says YOUR ACTION ITEM ends up beside a rail that does not.
   */
  suggestedOwnerPersonId: string | null
  /** The suggested owner's name, once the roster resolved a person (#16306). */
  ownerName?: string | null
  /**
   * How firmly it was said, as the card words it (#14376) — "Proposed",
   * "Decided", or "Assigned · Suggested owner: Maria (named in the quote)".
   * Null renders nothing: every row extracted before #14376, and every
   * non-EXTRACTION row, which nobody extracted. Optional for the same reason
   * as `readingFromQuote`.
   */
  commitmentLabel?: string | null
  /**
   * How formally a DECISION was decided, derived — never stored (#15565,
   * ADR-0134 § 3). Null on every card that is not a decision. A decision
   * card shows this INSTEAD of {@link RecordCard.commitmentLabel}: the
   * ladder is the decision's reading of its level, and "Decided" beside
   * "Decision language heard" would claim more than the quote does.
   * Optional for the same reason as `readingFromQuote`.
   */
  formality?: DecisionFormalityRead | null
  /** Always a sentence; see {@link NO_DUE_DATE}. */
  dueLabel: string
  /**
   * The due date AS SPOKEN, or null — what the card's Edit form opens on
   * (#14244). `dueLabel` is for display and says {@link NO_DUE_DATE} instead
   * of null, which an edit field must not pre-fill. Optional for the same
   * reason as `readingFromQuote`.
   */
  dueText?: string | null
  /** `Counts toward <goal>`, or null when the reading names no goal. */
  planGoalLabel: string | null
  /** The goal's id and title, for the rail's "Connected to" block. */
  planGoal: { id: string; title: string } | null
  /**
   * The `Priority` accepting this item minted, or null (#14239, ADR-0112 § 5).
   *
   * The link a card draws to "where this went" now that an accepted action
   * is a Priority rather than a `Task`. Taken from the row's
   * `acceptedPriority` — the durable `PriorityExtractedItem` minted-from
   * edge — so neither a hand-added link on the reading nor a missing
   * reading can change it. Null on anything that minted nothing. Optional
   * only so card literals written before it existed still type-check; the
   * builder always sets it.
   */
  acceptedPriority?: { id: string; title: string } | null
  /** C5. Null when the row carries no score; the surface decides whether to show it. */
  confidence: number | null
  /** C6 — the viewer is this item's suggested owner. */
  isYours: boolean
  taskId: string | null
  /** The task's status, for the accepted item's checkbox (#16306). */
  taskStatus?: string | null
  issueId: string | null
  dismissedReason: string | null
  createdById: string | null
  /** True while a machine's proposal is still a machine's proposal. */
  aiLabelled: boolean
  /** True for a PROPOSED row — what C4's filter narrows to. */
  needsConfirming: boolean
  /**
   * The `decision` Belief this card reads as (#16759), or null. An accepted
   * decision carrying one draws as the `DecisionCell`, linking to
   * `/decisions/[id]`; a suggested one keeps its Accept / Dismiss card.
   * Optional for the same reason as `readingFromQuote`.
   */
  decisionId?: string | null
  /** Its note in a gathered list (#16749); may the reader review there. */
  sourceNote?: { id: string; title: string } | null
  sourceNoteCanWrite?: boolean
}

/** A summary topic with the cards that were filed under it. */
export type RecordTopic = {
  heading: string
  body: string
  cards: RecordCard[]
  /**
   * This group is the catch-all, not a topic the summary wrote.
   *
   * Rendered with a heading that says so. Never merged into a real topic:
   * the difference between "the summary put this under Attendance" and "we
   * could not tell" is exactly the difference the epic's rule 1 protects.
   */
  unfiled: boolean
}

/**
 * Everything `/meetings/[id]`'s record renders, already decided.
 *
 * A PLAIN data object with no Prisma types and no Dates that need a zone: the
 * composed read (`record-data.ts`) does every lookup and every format, and
 * the component does none. That seam is what lets the whole record be
 * asserted from literals — and it is what keeps the server/client boundary
 * honest, because nothing in here has to cross it as a live object.
 */
export type MeetingRecordViewData = {
  /**
   * Where each citing card came from, keyed by item id (#13870, moved onto
   * the card by #13939).
   *
   * Built on the SERVER over the whole transcript in one read, rather than a
   * query per card. A card with no entry gets no quote button: either it
   * cannot cite a transcript at all — a hand-typed row has no line behind it —
   * or this meeting has no transcript to cite, and a button that opens onto
   * nothing is worse than no button.
   */
  provenance: Record<string, RecordItemProvenance>
  meetingId: string
  /**
   * The meeting's NOTE — the key the record's artifacts live on (#14238,
   * ADR-0112 § 1: artifact on Note, run on Meeting). Null for a meeting that
   * has no note yet: nobody recorded it and nothing was summarised, so its
   * record is the run's own highlights and typed items alone.
   */
  noteId: string | null
  /**
   * The meeting HAS a note, and this viewer may not read it (#14736): a
   * private note on a meeting they can see. Its summary, items, recordings
   * and transcript are absent from this view, `noteId` is null, and the page
   * says the notes are private rather than previewing them. Absent means no.
   */
  notePrivate?: boolean
  /** "September 15", in the VIEWER's zone. Formatted by the caller. */
  dateLabel: string
  /** "42 minutes", or null when nothing measured the meeting. */
  durationLabel: string | null
  attendeeNames: string[]
  /** #13807's opening paragraph, already run through {@link openingParagraphForViewer}. */
  openingParagraph: string | null
  /** Null when no summary was produced — the counts go with it. */
  counts: MeetingSummaryCounts | null
  topics: RecordTopic[]
  /** How many cards exist across every topic, before any filtering. */
  cardCount: number
  /** How many of them name the viewer as suggested owner (C6). */
  yoursCount: number
  degradation: RecordDegradation
  /**
   * The viewer's own typed notes. Null when they typed none.
   *
   * Since #13809 this is what the notes panel OPENS ONTO rather than what
   * it displays, so the panel renders whether or not this is null — the
   * person who took no notes during the meeting is exactly the person who
   * now wants to add the number that was misheard. `savedAt` rides along
   * because an empty note that WAS saved is not the same state as one that
   * has never existed, and `text` alone cannot tell them apart (see
   * `MeetingNotes.initialSavedAt`).
   *
   * On the C3 branch below they are also the CONTENT rather than a
   * supporting detail, so the panel renders them expanded (#13810).
   */
  notes: { lineCount: number; text: string; savedAt: Date | null } | null
  /**
   * C3 — nobody recorded this meeting, so the notes ARE the record
   * (#13810). Derived from the absence of a transcript by
   * {@link isNotRecordedRecord}; never stored.
   */
  notRecorded: boolean
  /** Null when this meeting has no transcript to read. */
  transcript: {
    href: string
    durationLabel: string | null
    uncertainSpans: number
  } | null
  /**
   * The meeting's latest `TranscriptionJob`, or null when none exists
   * (#14060).
   *
   * The column set has been on the table since #13699 and no rendered
   * surface read it, which is why `degradation === 'EMPTY'` had to stand for
   * three different facts — still working, failed, and genuinely nothing —
   * and said the most pessimistic of the three. {@link recordProcessingPosture}
   * is what splits them; this is the input it needs.
   */
  transcription: MeetingTranscriptionStatus | null
}

/** What of the record survived generation. Every state renders something. */
export type RecordDegradation =
  | 'FULL'
  | 'NO_SUMMARY'
  | 'NO_ITEMS'
  | 'TRANSCRIPT_ONLY'
  | 'EMPTY'

/**
 * `725000` -> `"12:05"`; past an hour, `"1:02:05"`.
 *
 * Carries rather than wrapping, because a two-hour IEP meeting's second
 * hour would otherwise cite timecodes that collide with its first.
 */
export function formatTimecode(ms: number | null): string | null {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return null

  const totalSeconds = Math.floor(ms / 1000)
  const seconds = totalSeconds % 60
  const minutes = Math.floor(totalSeconds / 60) % 60
  const hours = Math.floor(totalSeconds / 3600)
  const pad = (value: number) => String(value).padStart(2, '0')

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`
}

/**
 * The span back out of a note-taker citation URL.
 *
 * `Evidence` has no span columns, so `graph-write-fold.ts` rides the span in
 * `sourceUrls` as `?startMs=&endMs=` on the citation (`/notes/<id>/transcript`
 * since #14265, `/notetaker/<id>` on older rows; only the query is read) — and
 * that module's own
 * comment names this reader as the thing that would eventually consume them.
 * REFUSES rather than defaults: a citation whose span cannot be read is a
 * reading that cannot be attached to a quote, and attaching it to the wrong
 * quote is the one failure worse than not attaching it at all.
 */
export function parseEvidenceSpan(
  sourceUrl: string,
): { startMs: number; endMs: number } | null {
  const query = sourceUrl.slice(sourceUrl.indexOf('?') + 1)
  if (!sourceUrl.includes('?')) return null

  const params = new URLSearchParams(query)
  const startMs = Number(params.get('startMs'))
  const endMs = Number(params.get('endMs'))
  if (params.get('startMs') === null || params.get('endMs') === null)
    return null
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null

  return { startMs, endMs }
}

/** The key both halves of the join agree on: the transcript span. */
export function interpretationKey(
  startMs: number | null,
  endMs: number | null,
): string | null {
  if (startMs === null || endMs === null) return null
  return `${startMs}:${endMs}`
}

/**
 * The reading's linked Priorities, falling back to the single goal an
 * interpretation built before #14239 carries.
 */
function linkedPrioritiesOf(
  read: RecordItemInterpretation | undefined,
): readonly LinkedPriority[] {
  if (read === undefined) return []
  if (read.linkedPriorities !== undefined) return read.linkedPriorities
  return read.planGoalId === null || read.planGoalTitle === null
    ? []
    : [
        {
          id: read.planGoalId,
          title: read.planGoalTitle,
          state: DEFAULT_ITEM_STATE,
        },
      ]
}

/**
 * The item list plus its interpretations, as cards.
 *
 * @param items the ordered union from `getMeetingRecordItems`.
 * @param interpretations keyed by {@link interpretationKey}.
 * @param viewerPersonId the acting `Person.id`, or null for a reader with
 *   no identity. Absent a viewer, nothing is "yours" — the C6 marker is a
 *   claim about WHO is reading, so it needs one.
 */
export function buildRecordCards(
  items: readonly MeetingRecordItem[],
  interpretations: ReadonlyMap<string, RecordItemInterpretation>,
  viewerPersonId: string | null,
): RecordCard[] {
  return items.map(row => {
    const key = interpretationKey(row.startMs, row.endMs)
    const read = key === null ? undefined : interpretations.get(key)

    // An extraction that nobody has confirmed is still a machine's proposal.
    // Accepting it converts it into a `Task` a person stands behind, so the
    // label comes off — that is the reversibility half of the epic's rules.
    const aiLabelled = row.source === 'EXTRACTION' && row.state === 'PROPOSED'

    const accepted = row.acceptedPriority ?? null
    // An accepted item nothing interpreted has no Belief to carry an edited
    // reading, so the reviewer's wording lives only on the Priority it
    // became — which is then the heading, not the quote (#14244).
    const acceptedTitle =
      row.source === 'EXTRACTION' &&
      row.state === 'ACCEPTED' &&
      read === undefined &&
      accepted !== null
        ? accepted.title
        : null
    const rawReading = read?.statement ?? acceptedTitle ?? row.text
    // Only cite when the citation says something the title does not. Compared
    // on the RAW string: presenting the fallback capitalizes it, and a
    // capital letter is not a reading — citing it would still be a quote of
    // itself.
    const quotedText =
      row.quotedText !== null && row.quotedText !== rawReading
        ? row.quotedText
        : null
    const readingFromQuote =
      row.source === 'EXTRACTION' &&
      read === undefined &&
      row.quotedText !== null &&
      row.quotedText === rawReading

    // An EXTRACTION's owner can come from the interpretation (the roster-
    // resolved name) or from the reading's own free text; on the other arms
    // there is no interpretation and `ownerText` is the assignee the author
    // picked, so the fallback sentence differs — see `NO_OWNER` (#14044).
    const ownerIsSuggestion = row.source === 'EXTRACTION'
    const resolvedOwner = read?.suggestedOwnerName ?? row.ownerText

    const planGoal = planGoalFromLinks(
      linkedPrioritiesOf(read),
      row.priorityId,
      row.source === 'EXTRACTION' &&
        row.kind === 'ACTION' &&
        row.state === 'ACCEPTED',
    )

    return {
      id: row.id,
      source: row.source,
      kind: row.kind,
      state: row.state,
      // PRESENTED, never raw (#14244 / #14063). The quote below is not.
      reading: presentReading(rawReading),
      readingFromQuote,
      quotedText,
      timecodeLabel: formatTimecode(row.startMs),
      ownerLabel:
        resolvedOwner ?? (ownerIsSuggestion ? NO_OWNER_SUGGESTED : NO_OWNER),
      suggestedOwnerPersonId: read?.suggestedOwnerPersonId ?? null,
      ownerName:
        read?.suggestedOwnerPersonId != null
          ? (read.suggestedOwnerName ?? null)
          : null,
      // The SPOKEN owner, not the roster's: the label says the name came from
      // the quote, and `ownerText` is the one `guardCommitment` checked there.
      commitmentLabel:
        row.source === 'EXTRACTION' && row.kind !== 'DECISION'
          ? commitmentLabel(row.commitmentLevel ?? null, row.ownerText)
          : null,
      formality: row.kind === 'DECISION' ? decisionCardFormality(row) : null,
      decisionId: row.decisionId ?? null,
      dueLabel: row.dueText ?? NO_DUE_DATE,
      dueText: row.dueText,
      planGoalLabel:
        planGoal === null ? null : `Counts toward ${planGoal.title}`,
      planGoal:
        planGoal === null ? null : { id: planGoal.id, title: planGoal.title },
      acceptedPriority:
        accepted === null ? null : { id: accepted.id, title: accepted.title },
      confidence: read?.confidence ?? null,
      isYours:
        viewerPersonId !== null &&
        read?.suggestedOwnerPersonId != null &&
        read.suggestedOwnerPersonId === viewerPersonId,
      taskId: row.taskId,
      taskStatus: row.taskStatus ?? null,
      issueId: row.issueId,
      dismissedReason: row.dismissedReason,
      createdById: row.createdById,
      aiLabelled,
      needsConfirming: row.state === 'PROPOSED',
    }
  })
}

/** C4's narrowing: the items a person has not confirmed yet. */
export function filterCardsNeedingConfirmation(
  cards: readonly RecordCard[],
): RecordCard[] {
  return cards.filter(card => card.needsConfirming)
}

/** The heading the catch-all group carries when it is used. */
export const UNFILED_TOPIC_HEADING = 'Also from this meeting'

/**
 * Words too common to carry a topic. Deliberately short: a long stoplist is
 * a vocabulary that drifts, and the scoring below only needs the words that
 * would otherwise match EVERY section.
 */
const TOPIC_STOPWORDS = new Set([
  'the',
  'and',
  'for',
  'with',
  'that',
  'this',
  'from',
  'will',
  'was',
  'were',
  'has',
  'have',
  'had',
  'are',
  'not',
  'but',
  'they',
  'them',
  'their',
  'there',
  'what',
  'when',
  'who',
  'how',
  'into',
  'about',
  'before',
  'after',
  'over',
  'out',
  'yet',
  'some',
  'any',
  'one',
  'two',
  'said',
  'says',
  'need',
  'needs',
  'team',
  'meeting',
  'nobody',
  'everyone',
  'still',
  'more',
  'than',
  'been',
])

/** Lowercase word tokens of four characters or more, minus the stoplist. */
function topicTokens(text: string): Set<string> {
  const tokens = text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(word => word.length >= 4 && !TOPIC_STOPWORDS.has(word))
  return new Set(tokens)
}

/**
 * File each card under the topic that talks about it.
 *
 * Scores shared vocabulary, heading weighted above body — a heading is the
 * topic's name, a body merely mentions things. A card whose best score is
 * zero is UNFILED rather than assigned to section 0, and the unfiled group
 * is rendered with its own heading so a reader is never told the summary
 * placed something it did not.
 *
 * Ties go to the earliest section, which makes the assignment total and
 * stable: the same inputs must not shuffle between renders.
 *
 * A meeting with no summary sections gets one unfiled group holding
 * everything — the NO_SUMMARY degradation, which still renders its items.
 */
export function assignCardsToTopics(
  sections: readonly (MeetingSummarySection & { sourceBody?: string })[],
  cards: readonly RecordCard[],
): RecordTopic[] {
  const topics: RecordTopic[] = sections.map(section => ({
    heading: section.heading,
    body: section.body,
    cards: [],
    unfiled: false,
  }))

  // Scored on the paragraph AS STORED when suppression rewrote it (#14244,
  // `summary-item-dedupe.ts`): the sentence that restated a card is exactly
  // the evidence of which topic the card belongs under, and dropping it from
  // the render must not also drop the card into the unfiled group.
  const sectionTokens = sections.map(section => ({
    heading: topicTokens(section.heading),
    body: topicTokens(section.sourceBody ?? section.body),
  }))

  const unfiled: RecordCard[] = []

  for (const card of cards) {
    const cardTokens = topicTokens(`${card.reading} ${card.quotedText ?? ''}`)

    let bestIndex = -1
    let bestScore = 0
    sectionTokens.forEach((tokens, index) => {
      let score = 0
      for (const token of cardTokens) {
        if (tokens.heading.has(token)) score += 3
        else if (tokens.body.has(token)) score += 1
      }
      if (score > bestScore) {
        bestScore = score
        bestIndex = index
      }
    })

    if (bestIndex === -1) unfiled.push(card)
    else topics[bestIndex].cards.push(card)
  }

  // A heading with neither a paragraph nor a card under it is an orphan —
  // what a section becomes when every sentence in it restated an item filed
  // elsewhere (#14244). A section the summary wrote with a body is never
  // dropped, card or no card.
  const kept = topics.filter(
    topic => topic.body.length > 0 || topic.cards.length > 0,
  )

  if (unfiled.length > 0) {
    kept.push({
      heading: UNFILED_TOPIC_HEADING,
      body: '',
      cards: unfiled,
      unfiled: true,
    })
  }

  return kept
}

// ---------------------------------------------------------------------------
// The rail (canvas C1, blocks 2 and 3)
// ---------------------------------------------------------------------------

/** One attendee, with what this meeting produced for them. */
export type RecordRailAttendee = {
  /** `Person.id`, or null for an anonymous-external participant. */
  personId: string | null
  name: string
  /** Items suggested to them that somebody has already confirmed. */
  confirmedCount: number
  /** Items suggested to them that nobody has confirmed yet. */
  waitingCount: number
}

/** One plan goal, with how much this meeting added to it. */
export type RecordRailGoal = {
  id: string
  title: string
  itemCount: number
}

/**
 * The rail's second and third blocks, derived from the cards.
 *
 * BOTH counts are about the SUGGESTED owner, and the wording follows from
 * that. "1 waiting on them" describes an unconfirmed suggestion, not a
 * commitment that person made — nobody has agreed to anything yet, which is
 * exactly why it is still waiting. "Confirmed 1 item" describes a suggestion
 * somebody accepted. Neither line says the attendee owes anything, and the
 * difference between the two is precisely the review step this page exists
 * to make visible.
 *
 * An attendee with no `Person` behind them (an email-only external
 * participant) can never be a suggested owner — `Belief.personId` is a
 * `Person` FK — so their counts are structurally zero rather than
 * coincidentally so. They are still LISTED: they were in the room.
 *
 * DISMISSED cards count toward neither. A reviewer said it was not a
 * commitment, and a rail that keeps counting it contradicts the card.
 */
export function summariseRecordRail(
  cards: readonly RecordCard[],
  roster: readonly { personId: string | null; name: string }[],
): { attendees: RecordRailAttendee[]; goals: RecordRailGoal[] } {
  const confirmed = new Map<string, number>()
  const waiting = new Map<string, number>()
  const goals = new Map<string, RecordRailGoal>()

  for (const card of cards) {
    if (card.state === 'DISMISSED') continue

    const ownerId = card.suggestedOwnerPersonId
    if (ownerId !== null) {
      const bucket = card.needsConfirming ? waiting : confirmed
      bucket.set(ownerId, (bucket.get(ownerId) ?? 0) + 1)
    }

    if (card.planGoal !== null) {
      const existing = goals.get(card.planGoal.id)
      goals.set(card.planGoal.id, {
        id: card.planGoal.id,
        title: card.planGoal.title,
        itemCount: (existing?.itemCount ?? 0) + 1,
      })
    }
  }

  return {
    attendees: roster.map(member => ({
      personId: member.personId,
      name: member.name,
      confirmedCount:
        member.personId === null ? 0 : (confirmed.get(member.personId) ?? 0),
      waitingCount:
        member.personId === null ? 0 : (waiting.get(member.personId) ?? 0),
    })),
    goals: [...goals.values()],
  }
}

/**
 * C6 — the opening sentence acknowledges the viewer's own items.
 *
 * "Suggests you", never "you owe" and never "you committed to". The
 * difference is the whole liability argument in #13791: the same content,
 * labelled as a reading rather than asserted as a fact.
 *
 * Prepended rather than woven into the stored paragraph: the paragraph is
 * SHARED (it is the same one the follow-up email sends), and rewriting it
 * per viewer would mean four readers seeing four summaries that disagree —
 * which is the thing #13807 persisted a summary to prevent.
 */
export function openingParagraphForViewer(
  openingParagraph: string | null,
  yoursCount: number,
): string | null {
  if (yoursCount <= 0) return openingParagraph

  const line =
    yoursCount === 1
      ? 'The note taker suggests you as the owner of one item below — check it.'
      : `The note taker suggests you as the owner of ${yoursCount} items below — check them.`

  return openingParagraph === null ? line : `${line} ${openingParagraph}`
}

/**
 * C3 — nobody recorded this meeting (#13810, canvas C3).
 *
 * DERIVED, never stored: a column would go stale the moment a recording
 * landed, which is the argument `record-posture.ts` makes at length for row
 * B. And not derived from the item list either — a meeting nobody recorded
 * still produces items, which is the whole point of #13802's hand-added arm.
 *
 * ## Why it takes `hasRecording` and not just `hasTranscript`
 *
 * The first spelling was `!hasTranscript` alone, and it was wrong in the one
 * direction that matters. A meeting that WAS recorded and whose transcription
 * failed — or has not finished — has no transcript either, and it would then
 * render C3's copy: *"No recording — Nothing was captured"*. That is a false
 * statement about what happened, on the branch whose entire argument is that
 * nothing is asserted without a source. It also made
 * `recordDegradation`'s `EMPTY` unreachable on the page, because every
 * transcript-less record took the C3 branch first — so the "nothing came out
 * of this meeting yet" card was dead code.
 *
 * So the question is whether a capture EXISTS, and the transcript stays in
 * the predicate only as the belt-and-braces direction: a transcript with no
 * surviving `Recording` row is still evidence that somebody recorded.
 *
 * Because it is derived, it flips in BOTH directions for free: B4's "Record
 * again" creates a recording and the next render is an ordinary recorded
 * record — degraded to `EMPTY` until the transcript lands, then whole — with
 * every hand-added item still on it.
 */
export function isNotRecordedRecord(input: {
  hasRecording: boolean
  hasTranscript: boolean
}): boolean {
  return !input.hasRecording && !input.hasTranscript
}

/**
 * Whether the AI disclosure belongs on the page yet.
 *
 * On a recorded record it is UNCONDITIONAL, and `meeting-record.tsx` says
 * why: "a disclosure that appears only when the page is confident is not a
 * disclosure". C3 does not weaken that rule, it precedes it — nothing has
 * been generated, so there is nothing to disclose, and a banner saying the
 * page was "written by AI from the recording and your notes" beside a
 * meeting with no recording and no generated text is simply false.
 *
 * The moment anything IS generated — a notes-only summary (#13807) — the
 * disclosure returns. Proposals found in the notes carry their own AI label
 * per card, which is the same labelling an extracted item gets and is
 * rendered by the control that produced them.
 */
export function shouldDiscloseAi(input: {
  notRecorded: boolean
  hasSummary: boolean
}): boolean {
  return !input.notRecorded || input.hasSummary
}

/**
 * Which layers of the record survived. Every answer renders something.
 *
 * The issue's rule is "every layer above the transcript degrades to it", so
 * this returns a state rather than a boolean: a page that only knows "the
 * summary is missing" cannot tell a reader whether anything else is.
 */
export function recordDegradation(input: {
  hasSummary: boolean
  hasItems: boolean
  hasTranscript: boolean
}): RecordDegradation {
  if (input.hasSummary && input.hasItems) return 'FULL'
  if (!input.hasSummary && input.hasItems) return 'NO_SUMMARY'
  if (input.hasSummary && !input.hasItems) return 'NO_ITEMS'
  return input.hasTranscript ? 'TRANSCRIPT_ONLY' : 'EMPTY'
}

/**
 * Which of the three "there is nothing here yet" cards the record renders.
 *
 * `NONE` is the ordinary answer and the common one: a record with content
 * says what it has and needs no card at all.
 */
export type RecordProcessingPosture = 'PROCESSING' | 'FAILED' | 'EMPTY' | 'NONE'

/**
 * Split `EMPTY` into the three facts it was hiding (#14060).
 *
 * {@link recordDegradation} answers "which layers survived", and cannot
 * answer "is anything still coming": a meeting whose transcription is
 * halfway through and one whose transcription died are the same `EMPTY` to
 * it. `meeting-record.tsx` rendered one card for both, and its own comment
 * named the ambiguity — *"the card for a meeting that WAS recorded and whose
 * transcription failed **or has not finished**"* — while the copy underneath
 * asserted the pessimistic reading of it.
 *
 * ## PROCESSING survives the first segments landing
 *
 * Segments commit as they are transcribed, so a running job carries the
 * record from `EMPTY` to `TRANSCRIPT_ONLY` before it finishes. Dropping the
 * processing state there would replace a moving page with a transcript link
 * and no explanation of why the summary is missing, half a minute before the
 * summary arrives. Both of those degradations therefore admit PROCESSING,
 * and every other one is a record with content — nothing is pending on the
 * page, whatever the job row says.
 *
 * ## C3 is not a degradation of this
 *
 * A meeting nobody recorded (#13810) owns every sentence on its own branch,
 * and a processing card there would claim a capture that does not exist. It
 * is checked first for that reason.
 */
export function recordProcessingPosture(input: {
  degradation: RecordDegradation
  notRecorded: boolean
  transcription: MeetingTranscriptionStatus | null
}): RecordProcessingPosture {
  if (input.notRecorded) return 'NONE'

  const nothingGenerated =
    input.degradation === 'EMPTY' || input.degradation === 'TRANSCRIPT_ONLY'
  if (!nothingGenerated) return 'NONE'

  if (isTranscriptionInFlight(input.transcription?.status ?? null)) {
    return 'PROCESSING'
  }

  // Past here the job is over (or absent) and only the fully empty record
  // has a card: a transcript on the page IS the record, degraded.
  if (input.degradation !== 'EMPTY') return 'NONE'

  return input.transcription?.status === 'FAILED' ? 'FAILED' : 'EMPTY'
}
